import React, { useState } from 'react';
import { User } from 'lucide-react';
import { getProfilePhotoUrl } from '../../utils/url';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ProfileAvatarProps {
  user: {
    username: string;
    profilePhoto?: string | null;
    profilePhotoThumbnail?: string | null;
  } | null | undefined;
  size?: AvatarSize;
  className?: string;
  onClick?: () => void;
  showBorder?: boolean;
  alt?: string;
}

const sizeClasses = {
  xs: 'w-4 h-4 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-16 h-16 text-xl',
  xl: 'w-24 h-24 text-2xl',
} as const;

const iconSizes = {
  xs: 8,
  sm: 16,
  md: 20,
  lg: 32,
  xl: 48,
} as const;

// Elke gebruiker een eigen neon hue-combinatie (zie DESIGN-SYSTEM.md)
const avatarGradients = [
  { bg: 'linear-gradient(135deg, #ff3d81, #7a3dff)', text: '#fff' },
  { bg: 'linear-gradient(135deg, #23e5db, #b8ff3d)', text: '#0a0a12' },
  { bg: 'linear-gradient(135deg, #7a3dff, #23e5db)', text: '#fff' },
  { bg: 'linear-gradient(135deg, #ff3d81, #23e5db)', text: '#0a0a12' },
  { bg: 'linear-gradient(135deg, #b8ff3d, #23e5db)', text: '#0a0a12' },
  { bg: 'linear-gradient(135deg, #ff8a3d, #ff3d81)', text: '#fff' },
] as const;

const gradientForUsername = (username: string) => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) | 0;
  }
  return avatarGradients[Math.abs(hash) % avatarGradients.length] ?? avatarGradients[0];
};

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  user,
  size = 'sm',
  className = '',
  onClick,
  showBorder = false,
  alt,
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  // Get user info with defaults
  const username = user?.username || 'User';
  const profilePhoto = user?.profilePhoto;
  const profilePhotoThumbnail = user?.profilePhotoThumbnail;
  const initials = username.charAt(0).toUpperCase();

  // Use thumbnail for small sizes (xs, sm, md), main image for larger sizes (lg, xl)
  const useMainImage = size === 'lg' || size === 'xl';
  const selectedPhoto = useMainImage ? profilePhoto : (profilePhotoThumbnail || profilePhoto);

  // Generate photo URL
  const photoUrl = selectedPhoto ? getProfilePhotoUrl(selectedPhoto) : null;
  const shouldShowImage = photoUrl && !imageError;

  const gradient = gradientForUsername(username);

  // Base classes
  const baseClasses = `
    ${sizeClasses[size]}
    rounded-full
    flex-shrink-0
    overflow-hidden
    ${showBorder ? 'ring-2 ring-neon-pink/60 ring-offset-2 ring-offset-canvas' : ''}
    ${onClick ? 'cursor-pointer' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div
      className={baseClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {shouldShowImage ? (
        <img
          src={photoUrl}
          alt={alt || `${username}'s profile picture`}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      ) : (
        // Fallback: gradient met initiaal
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ background: gradient.bg }}
        >
          {username ? (
            <span
              className="font-bold select-none"
              style={{ color: gradient.text }}
            >
              {initials}
            </span>
          ) : (
            <User
              style={{ color: gradient.text }}
              size={iconSizes[size]}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileAvatar;
